import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { clientsAPI, vendorsAPI, fetchData } from '../utils/api';
import '../css/ClientManagement.css';
import ExecutiveOfficerManagement from '../components/ExecutiveOfficerManagement';

export const ClientManagement = () => {
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('customers');
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showOpeningBalanceModal, setShowOpeningBalanceModal] = useState(false);
  const [showOfficerManagement, setShowOfficerManagement] = useState(false);
  const [executiveOfficers, setExecutiveOfficers] = useState([]);
  const [openingBalanceAmount, setOpeningBalanceAmount] = useState('');
  const [selectedClientForBalance, setSelectedClientForBalance] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [readOnly, setReadOnly] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    gst_number: '',
    contact_person: '',
    executive_officer_name: '',
    client_type: 'Customer',
  });

  // Normalize role and capabilities
  const role = (user?.role || '').toString().toLowerCase();
  const isAdmin = role === 'admin';
  const isSalesManager = role.includes('sales');
  const isPurchaseManager = role.includes('purchase');
  const canEdit = isAdmin || role.includes('manager');

  // Determine which tabs this user can access
  let availableTabs = [];
  if (isAdmin) {
    availableTabs = ['customers', 'vendors'];
  } else if (isSalesManager) {
    availableTabs = ['customers'];
  } else if (isPurchaseManager) {
    availableTabs = ['vendors'];
  } else {
    // normal user: allow both tabs but only view mode
    availableTabs = ['customers', 'vendors'];
  }

  // Ensure activeTab is valid for current role
  useEffect(() => {
    if (!availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0]);
    }
  }, [role]); // run when role changes / user loads

  useEffect(() => {
    loadData();
    loadExecutiveOfficers();
  }, [token]);

  const loadExecutiveOfficers = async () => {
    try {
      const data = await fetchData('/api/executive-officers');
      setExecutiveOfficers(data);
    } catch (error) {
      // console.error('Error loading executive officers:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const clientsRes = await clientsAPI.getAll();

      // Normalize response to array
      const allClients = Array.isArray(clientsRes.data)
        ? clientsRes.data
        : clientsRes.data?.data || [];

      // Split by client_type (case-insensitive)
      const customersData = allClients.filter(
        (c) => (c.client_type || '').toString().toLowerCase() === 'customer'
      );
      const vendorsData = allClients.filter(
        (c) => (c.client_type || '').toString().toLowerCase() === 'vendor'
      );

      setCustomers(customersData);
      setVendors(vendorsData);
    } catch (error) {
      // console.error('Error loading data:', error);
      // console.error('Error response:', error?.response?.data);
      setError(error?.response?.data?.message || 'Failed to load data. Please try again.');
      setCustomers([]);
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const api = clientsAPI;

      const requestData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        gst_number: formData.gst_number,
        contact_person: formData.contact_person,
        executive_officer_name: formData.executive_officer_name,
        notes: '',
        account_status: 'Active',
        client_type: formData.client_type
      };

      if (editingId) {
        await api.update(editingId, requestData);
        alert('Updated successfully!');
      } else {
        await api.create(requestData);
        alert('Created successfully!');
      }

      loadData();
      setShowModal(false);
      resetForm();
    } catch (error) {
      // console.error('Error saving data:', error);
      // console.error('Error details:', error?.response?.data);
      alert(error?.response?.data?.message || 'Error saving data. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await clientsAPI.delete(id);
        alert('Deleted successfully!');
        loadData();
      } catch (error) {
        // console.error('Error deleting data:', error);
        alert(error?.response?.data?.message || 'Error deleting data. Please try again.');
      }
    }
  };

  const handleClientNameClick = (clientId) => {
    if (activeTab === 'customers') {
      navigate(`/sales-account/${clientId}`);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      gst_number: '',
      contact_person: '',
      executive_officer_name: '',
      client_type: activeTab === 'customers' ? 'Customer' : 'Vendor',
    });
    setEditingId(null);
    setReadOnly(false);
  };

  const handleEdit = (item) => {
    setReadOnly(false);
    setFormData({
      name: item.name,
      email: item.email,
      phone: item.phone,
      address: item.address,
      gst_number: item.gst_number || '',
      contact_person: item.contact_person || '',
      executive_officer_name: item.executive_officer_name || '',
      client_type: item.client_type || (activeTab === 'customers' ? 'Customer' : 'Vendor'),
    });
    setEditingId(item.id);
    setShowModal(true);
  };

  const handleView = (item) => {
    setReadOnly(true);
    setFormData({
      name: item.name,
      email: item.email,
      phone: item.phone,
      address: item.address,
      gst_number: item.gst_number || '',
      contact_person: item.contact_person || '',
      executive_officer_name: item.executive_officer_name || '',
      client_type: item.client_type || (activeTab === 'customers' ? 'Customer' : 'Vendor'),
    });
    setEditingId(item.id);
    setShowModal(true);
  };

  const handleAddNew = () => {
    resetForm();
    // Only admins or editors can create new records
    if (!canEdit) {
      alert('You do not have permission to add new records.');
      return;
    }
    setShowModal(true);
  };

  const handleOpenBalanceModal = async (client) => {
    setSelectedClientForBalance(client);
    setOpeningBalanceAmount('');
    try {
      const api = activeTab === 'customers' ? clientsAPI : vendorsAPI;
      const res = await api.getOpeningBalance(client.id);
      if (res.data.success) {
        // For customers, it might return opening_balance directly or inside an object
        // Based on backend: Customers returns {success: true, opening_balance: X}
        // Vendors returns {success: true, account: {opening_balance: X}}
        const balance = activeTab === 'customers' 
          ? res.data.opening_balance 
          : (res.data.account?.opening_balance || 0);
        
        if (balance !== undefined && balance !== 0) {
          setOpeningBalanceAmount(balance.toString());
        }
      }
    } catch (error) {
      console.error('Error fetching existing opening balance:', error);
    }
    setShowOpeningBalanceModal(true);
  };

  const saveOpeningBalance = async () => {
    if (!selectedClientForBalance) return;
    if (!openingBalanceAmount) {
      alert('Please enter an amount');
      return;
    }

    const amount = parseFloat(openingBalanceAmount);
    if (isNaN(amount)) {
      alert('Invalid amount');
      return;
    }

    try {
      const api = activeTab === 'customers' ? clientsAPI : vendorsAPI;
      
      // Check if we already have a balance to decide between POST (set) and PUT (update)
      // Since we also just fetched it in handleOpenBalanceModal, we can check if the current input 
      // is different from 0 or just always use our new update endpoint if it exists.
      // But the safest way is to try update if existing, or just use update if we want to support both.
      // Let's use getOpeningBalance again or rely on state. 
      // Actually, my backend implementation for POST uses ON DUPLICATE KEY UPDATE in some places, 
      // but the user specifically asked for an edit that doesn't mess up totals.
      
      const res = await api.getOpeningBalance(selectedClientForBalance.id);
      const existingBalance = activeTab === 'customers' 
        ? res.data.opening_balance 
        : (res.data.account?.opening_balance || 0);

      if (existingBalance !== undefined && existingBalance !== 0) {
        await api.updateOpeningBalance(selectedClientForBalance.id, {
          opening_balance: amount
        });
      } else {
        await api.setOpeningBalance(selectedClientForBalance.id, {
          opening_balance: amount
        });
      }

      alert('Opening balance saved successfully!');
      setShowOpeningBalanceModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving opening balance:', error);
      alert(error?.response?.data?.error || 'Failed to save opening balance');
    }
  };

  const data = activeTab === 'customers' ? customers : vendors;
  const title = activeTab === 'customers' ? 'Customers' : 'Vendors';

  // Filter data based on search term
  const filteredData = data.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.phone?.includes(searchTerm)
  );

  // Reset pagination when tab or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm]);

  // Get current posts
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="h3 mb-1">
                <i className="bi bi-people me-2"></i>
                Client Management
              </h1>
              <p className="text-muted mb-0">Manage your customers and vendors</p>
            </div>
            {canEdit && (
              <button
                className="btn btn-primary"
                onClick={handleAddNew}
              >
                <i className="bi bi-plus-circle me-2"></i>
                Add New Client
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="row mb-3">
          <div className="col-12">
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              <i className="bi bi-exclamation-circle me-2"></i>
              {error}
              <button type="button" className="btn-close" onClick={() => setError(null)}></button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs and Search Section */}
      <div className="row mb-3">
        <div className="col-md-6">
          <nav>
            <div className="nav nav-tabs" id="nav-tab" role="tablist">
              {availableTabs.includes('customers') && (
                <button
                  className={`nav-link ${activeTab === 'customers' ? 'active' : ''}`}
                  id="nav-customers-tab"
                  type="button"
                  role="tab"
                  aria-controls="nav-customers"
                  aria-selected={activeTab === 'customers'}
                  onClick={() => {
                    setActiveTab('customers');
                    setSearchTerm('');
                  }}
                  style={{
                    color: activeTab === 'customers' ? '#495057' : '#6c757d',
                    backgroundColor: activeTab === 'customers' ? '#fff' : 'transparent',
                    borderColor: activeTab === 'customers' ? '#dee2e6 #dee2e6 #fff' : 'transparent'
                  }}
                >
                  <i className="bi bi-shop me-2"></i>
                  Customers
                </button>
              )}

              {availableTabs.includes('vendors') && (
                <button
                  className={`nav-link ${activeTab === 'vendors' ? 'active' : ''}`}
                  id="nav-vendors-tab"
                  type="button"
                  role="tab"
                  aria-controls="nav-vendors"
                  aria-selected={activeTab === 'vendors'}
                  onClick={() => {
                    setActiveTab('vendors');
                    setSearchTerm('');
                  }}
                  style={{
                    color: activeTab === 'vendors' ? '#495057' : '#6c757d',
                    backgroundColor: activeTab === 'vendors' ? '#fff' : 'transparent',
                    borderColor: activeTab === 'vendors' ? '#dee2e6 #dee2e6 #fff' : 'transparent'
                  }}
                >
                  <i className="bi bi-building me-2"></i>
                  Vendors
                </button>
              )}
            </div>
          </nav>
        </div>

        {/* Search Bar */}
        <div className="col-md-6">
          <div className="input-group">
            <span className="input-group-text bg-light border-end-0">
              <i className="bi bi-search text-muted"></i>
            </span>
            <input
              type="text"
              className="form-control border-start-0"
              placeholder={`Search ${activeTab} by name, email, or phone...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="btn btn-outline-secondary"
                onClick={() => setSearchTerm('')}
                type="button"
              >
                <i className="bi bi-x-circle"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Results Counter */}
      <div className="row mb-2">
        <div className="col-12">
          <p className="text-muted small">
            Showing <strong>{filteredData.length}</strong> of <strong>{data.length}</strong> {title.toLowerCase()}
            {searchTerm && <span> (filtered by: <strong>"{searchTerm}"</strong>)</span>}
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-hover align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>
                            <i className="bi bi-building me-2"></i>
                            Company Name
                          </th>
                          <th>
                            <i className="bi bi-geo-alt me-2"></i>
                            Address Details
                          </th>
                          <th>
                            <i className="bi bi-telephone me-2"></i>
                            Contact Number
                          </th>
                          <th>
                            <i className="bi bi-envelope me-2"></i>
                            Email ID
                          </th>
                          <th style={{ minWidth: '140px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.length > 0 ? (
                          currentItems.map((item) => (
                            <tr key={item.id}>
                              <td>
                                <div className="fw-bold">
                                  {item.name}
                                </div>
                                {item.executive_officer_name && (
                                  <small className="text-muted d-block">
                                    Officer: {item.executive_officer_name}
                                  </small>
                                )}
                              </td>
                              <td>
                                <span className="text-muted">{item.address || 'N/A'}</span>
                              </td>
                              <td>
                                <a href={`tel:${item.phone}`} className="text-decoration-none">
                                  {item.phone || 'N/A'}
                                </a>
                              </td>
                              <td>
                                <a href={`mailto:${item.email}`} className="text-decoration-none">
                                  {item.email || 'N/A'}
                                </a>
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <button
                                    className="btn btn-sm btn-info me-2"
                                    onClick={() => handleView(item)}
                                    title="View Details"
                                  >
                                    <i className="bi bi-eye"></i>
                                  </button>

                                  <button
                                    className="btn btn-sm btn-secondary me-2"
                                    onClick={() => handleOpenBalanceModal(item)}
                                    title="Opening Balance"
                                  >
                                    <i className="bi bi-wallet2"></i>
                                  </button>

                                  {canEdit && (
                                    <>
                                      <button
                                        className="btn btn-sm btn-warning me-2"
                                        onClick={() => handleEdit(item)}
                                        title="Edit"
                                      >
                                        <i className="bi bi-pencil"></i>
                                      </button>
                                      <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() => handleDelete(item.id)}
                                        title="Delete"
                                      >
                                        <i className="bi bi-trash"></i>
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center py-4 text-muted">
                              {searchTerm ? (
                                <>
                                  <i className="bi bi-search me-2"></i>
                                  No {title.toLowerCase()} found matching "<strong>{searchTerm}</strong>"
                                </>
                              ) : (
                                <>
                                  No {title.toLowerCase()} found.
                                  {canEdit && (
                                    <> Click "Add New {title === 'Customers' ? 'Customer' : 'Vendor'}" to get started.</>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Controls */}
                  {filteredData.length > itemsPerPage && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div className="text-muted small">
                        Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredData.length)} of {filteredData.length} entries
                      </div>
                      <nav aria-label="Page navigation">
                        <ul className="pagination mb-0">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => paginate(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              Previous
                            </button>
                          </li>
                          {(() => {
                            const totalPages = Math.ceil(filteredData.length / itemsPerPage);
                            const maxVisible = 5;
                            let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                            let endPage = Math.min(totalPages, startPage + maxVisible - 1);

                            if (endPage - startPage + 1 < maxVisible) {
                              startPage = Math.max(1, endPage - maxVisible + 1);
                            }

                            return [...Array(endPage - startPage + 1)].map((_, index) => {
                              const pageNumber = startPage + index;
                              return (
                                <li
                                  key={pageNumber}
                                  className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => paginate(pageNumber)}
                                  >
                                    {pageNumber}
                                  </button>
                                </li>
                              );
                            });
                          })()}
                          <li className={`page-item ${currentPage === Math.ceil(filteredData.length / itemsPerPage) ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => paginate(currentPage + 1)}
                              disabled={currentPage === Math.ceil(filteredData.length / itemsPerPage)}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className={`bi ${editingId ? 'bi-pencil-square' : 'bi-plus-circle'} me-2`}></i>
                  {editingId ? (readOnly ? 'View ' : 'Edit ') : 'Add New '} {formData.client_type}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {/* Company/Client Name */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="bi bi-building me-2 text-primary"></i>
                      {formData.client_type} Name *
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={`Enter ${formData.client_type.toLowerCase()} name`}
                      required
                      readOnly={readOnly}
                    />
                  </div>

                  {/* Client Type Dropdown */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="bi bi-tags me-2 text-primary"></i>
                      Client Type *
                    </label>
                    <select
                      className="form-select"
                      value={formData.client_type}
                      onChange={(e) => setFormData({ ...formData, client_type: e.target.value })}
                      required
                      disabled={readOnly}
                    >
                      <option value="Customer">Customer</option>
                      <option value="Vendor">Vendor</option>
                    </select>
                  </div>

                  {/* GST Number */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="bi bi-card-text me-2 text-primary"></i>
                      GST Number
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.gst_number}
                      onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                      placeholder="Enter GST Number"
                      readOnly={readOnly}
                    />
                  </div>

                  {/* Executive Officer Name */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label className="form-label fw-bold m-0">
                        <i className="bi bi-person-badge me-2 text-primary"></i>
                        Executive Officer Name
                      </label>
                      {/* {!readOnly && (
                        <button
                          className="btn btn-sm text-primary text-decoration-none p-0"
                          type="button"
                          onClick={() => setShowOfficerManagement(true)}
                          title="Manage Executive Officers"
                        >
                          <i className="bi bi-gear me-1"></i> Manage List
                        </button>
                      )} */}
                    </div>
                    <select
                      className="form-select"
                      value={formData.executive_officer_name}
                      onChange={(e) => setFormData({ ...formData, executive_officer_name: e.target.value })}
                      disabled={readOnly}
                    >
                      <option value="">Select Executive Officer</option>
                      {executiveOfficers.map(officer => (
                        <option key={officer.id} value={officer.name}>{officer.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Contact Person */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="bi bi-person me-2 text-primary"></i>
                      Contact Person
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.contact_person}
                      onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                      placeholder="Enter contact person name"
                      readOnly={readOnly}
                    />
                  </div>

                  {/* Email */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="bi bi-envelope me-2 text-primary"></i>
                      Email ID
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Enter email address"
                      readOnly={readOnly}
                    />
                  </div>

                  {/* Phone */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="bi bi-telephone me-2 text-primary"></i>
                      Contact Number
                    </label>
                    <input
                      type="tel"
                      className="form-control"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="Enter phone number"
                      readOnly={readOnly}
                    />
                  </div>

                  {/* Address */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="bi bi-geo-alt me-2 text-primary"></i>
                      Address Details
                    </label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Enter full address"
                      readOnly={readOnly}
                    ></textarea>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                    Cancel
                  </button>

                  {readOnly ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => setShowModal(false)}
                    >
                      Close
                    </button>
                  ) : (
                    <button type="submit" className="btn btn-primary">
                      <i className="bi bi-check-circle me-2"></i>
                      {editingId ? 'Update' : 'Save'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Opening Balance Modal */}
      {showOpeningBalanceModal && (
        <div className="modal-overlay" onClick={() => setShowOpeningBalanceModal(false)}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-wallet2 me-2"></i>
                  Set Opening Balance
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowOpeningBalanceModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-bold">Client Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedClientForBalance?.name || ''}
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Opening Balance Amount</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="0"
                    value={openingBalanceAmount}
                    onChange={(e) => setOpeningBalanceAmount(e.target.value)}
                  />
                  <small className="text-muted">
                    This amount will be set as the initial balance for this {activeTab === 'customers' ? 'customer' : 'vendor'}.
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowOpeningBalanceModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={saveOpeningBalance}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Executive Officer Management Modal */}
      {showOfficerManagement && (
        <div className="modal-overlay" onClick={() => setShowOfficerManagement(false)}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <ExecutiveOfficerManagement
                onClose={() => {
                  setShowOfficerManagement(false);
                  loadExecutiveOfficers();
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
